import React, { useMemo, useState } from 'react';
import {
  AppShell,
  Navbar,
  Anchor,
  MediaQuery,
  Header,
  Burger,
  Image,
  Flex,
  Button,
  Loader,
  Text,
  createStyles,
  Group,
  Transition,
} from '@mantine/core';
import { useMantineTheme } from '@mantine/styles';
import { Outlet } from 'react-router';
import { NavLink } from 'react-router-dom';
import routes from '../../routes';
import { useMsal } from '@azure/msal-react';
import { loginRequest } from '../../authConfig';
import { InteractionStatus } from '@azure/msal-browser';
import useAccountInformation from '../../Hooks/use-user-data';

const useStyles = createStyles((theme) => ({
  navbar: {
    [theme.fn.largerThan('lg')]: {
      display: 'none',
    },
  },

  header: {
    [theme.fn.smallerThan('lg')]: {
      display: 'none',
    },
  },
}));

function Layout() {
  const theme = useMantineTheme();
  const { classes } = useStyles();
  const { instance, inProgress } = useMsal();
  const { loggedIn, name, isAdministrator } = useAccountInformation();
  const [opened, setOpened] = useState(false);

  const logo = useMemo(() => {
    return (
      <Anchor href={'https://www.avensia.com'}>
        <Image src={'/images/logo.png'} alt={'Avensia logo'} height={30} fit={'contain'} />
      </Anchor>
    );
  }, []);

  const howToLink = useMemo(() => {
    return (
      loggedIn && (
        <Anchor
          key={'how-to'}
          href={'https://avensiaab.sharepoint.com/sites/BUCommerce/SitePages/How-to-use-the-Tech-Radar.aspx'}
          style={{ textDecoration: 'none', borderRadius: 4 }}
          target="_blank"
        >
          <Text
            fz={'sm'}
            sx={{
              borderRadius: 4,
              fontWeight: 600,
            }}
            color={'white'}
          >
            {'How to use'}
          </Text>
        </Anchor>
      )
    );
  }, [loggedIn]);

  const routeElements = useMemo(() => {
    return routes
      .filter(
        (r) =>
          r.displayInNavigation === 'All' ||
          (r.displayInNavigation === 'Admins' && isAdministrator) ||
          (r.displayInNavigation === 'LoggedIn' && loggedIn),
      )
      .map((route) => {
        return (
          <NavLink
            key={route.path}
            to={route.path}
            style={{ textDecoration: 'none', borderRadius: 4 }}
            onClick={() => setOpened(false)}
            end
          >
            {({ isActive }) => (
              <Text
                fz={'sm'}
                sx={{
                  borderRadius: 4,
                  fontWeight: 600,
                }}
                color={'white'}
              >
                {route.label}
              </Text>
            )}
          </NavLink>
        );
      });
  }, [isAdministrator, loggedIn]);

  const userElements = useMemo(() => {
    return inProgress !== InteractionStatus.None ? (
      <Loader />
    ) : loggedIn ? (
      <Group spacing={'md'}>
        <Text fz={'sm'} sx={{ color: 'white' }}>
          {'Logged in as '}
          <b>{name}</b>
        </Text>
        <Button color={'avensia-turqoise'} component={NavLink} to={'/logout'}>
          Log out
        </Button>
      </Group>
    ) : (
      <Group spacing={'md'}>
        <Button fullWidth onClick={() => instance.loginPopup(loginRequest)}>
          Log in
        </Button>
      </Group>
    );
  }, [inProgress, instance, loggedIn, name]);

  return (
    <AppShell
      styles={{
        main: {
          background: theme.colorScheme === 'dark' ? theme.colors.dark[8] : theme.colors.gray[0],
        },
      }}
      navbarOffsetBreakpoint="md"
      header={
        <Header
          height={{ base: 50, lg: 60 }}
          py="xs"
          px={{ base: 'xs', lg: 40 }}
          sx={{ backgroundColor: theme.colors['avensia-blue'][9], borderBottom: 'none' }}
        >
          <MediaQuery largerThan="lg" styles={{ display: 'none' }}>
            <Burger
              opened={opened}
              onClick={() => setOpened((o) => !o)}
              size="sm"
              color={theme.colors.gray[6]}
              mr="sm"
            />
          </MediaQuery>
          <Flex justify={'space-between'} align={'center'} className={classes.header}>
            <Group>{logo}</Group>
            <Group spacing={32}>
              {routeElements}
              {howToLink}
              {userElements}
            </Group>
          </Flex>
        </Header>
      }
      navbar={
        <Transition mounted={opened} transition={'slide-right'} timingFunction="ease-in-out" duration={300}>
          {(styles) => (
            <Navbar
              p="md"
              className={classes.navbar}
              maw={{ base: '100%', xs: 400 }}
              width={{ lg: 0 }}
              sx={{ backgroundColor: theme.colors['avensia-blue'][9], borderRight: 'none' }}
            >
              <Flex style={styles} direction="column" justify="space-between" h={'100%'}>
                <Navbar.Section
                  grow
                  pt={'lg'}
                  px={'sm'}
                  sx={{ borderTop: '1px solid', borderColor: theme.colors.gray[2] }}
                >
                  {routeElements}
                  {howToLink}
                </Navbar.Section>
                <Navbar.Section pt={'lg'} px={'sm'} sx={{ borderTop: '1px solid', borderColor: theme.colors.gray[2] }}>
                  {userElements}
                </Navbar.Section>
              </Flex>
            </Navbar>
          )}
        </Transition>
      }
    >
      <Outlet />
    </AppShell>
  );
}

export default Layout;
